import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight, BookOpen } from "lucide-react";
import blogHero from "@/assets/blog-hero.jpg";

const Blog = () => {
  const articles = [
    {
      title: "What is a Random Number Generator? Complete Guide for 2025",
      slug: "/blog/what-is-random-number-generator",
      excerpt: "Learn everything about random number generators (RNGs), how they work, different types (true vs pseudo-random), and why cryptographic security matters for your applications.",
      category: "Fundamentals",
      readTime: "8 min read",
      date: "2025-01-15",
      keywords: ["random number generator", "RNG", "CSPRNG", "cryptography"]
    },
    {
      title: "Probability vs Statistics: Key Differences Explained Simply",
      slug: "/blog/probability-vs-statistics",
      excerpt: "Understand the fundamental differences between probability and statistics, when to use each, and how random number generators play a crucial role in both fields.",
      category: "Theory",
      readTime: "7 min read",
      date: "2025-01-12",
      keywords: ["probability", "statistics", "mathematics", "data analysis"]
    },
    {
      title: "Monte Carlo Simulation: Practical Guide with Examples",
      slug: "/blog/monte-carlo-simulation-guide",
      excerpt: "Discover how Monte Carlo simulations use random sampling to solve complex problems in finance, engineering, and science. Includes practical examples and implementation tips.",
      category: "Applications",
      readTime: "10 min read",
      date: "2025-01-10",
      keywords: ["Monte Carlo", "simulation", "risk analysis", "forecasting"]
    },
    {
      title: "Random Sampling Methods: Simple, Stratified, and Cluster",
      slug: "/blog/random-sampling-methods",
      excerpt: "Master different random sampling techniques used in research and statistics. Learn when to use simple random sampling, stratified sampling, or cluster sampling.",
      category: "Research Methods",
      readTime: "9 min read",
      date: "2025-01-08",
      keywords: ["random sampling", "research methods", "survey design", "statistics"]
    },
    {
      title: "How Random Number Generators Power Modern Technology",
      slug: "/blog/rng-in-modern-technology",
      excerpt: "Explore the critical role of random number generation in cryptography, gaming, simulations, AI training, and blockchain. Real-world use cases explained.",
      category: "Applications",
      readTime: "11 min read",
      date: "2025-01-05",
      keywords: ["technology", "cryptography", "blockchain", "AI", "gaming"]
    },
    {
      title: "Fair Decision Making: Using Randomness to Eliminate Bias",
      slug: "/blog/fair-decision-making-randomness",
      excerpt: "Learn how randomization creates fairness in lotteries, clinical trials, jury selection, and everyday decisions. The science behind unbiased random selection.",
      category: "Ethics & Society",
      readTime: "6 min read",
      date: "2025-01-03",
      keywords: ["fairness", "bias", "randomization", "ethics", "decision making"]
    }
  ];

  return (
    <Layout>
      <SEO 
        title="Blog - Random Number Generator Guides & Tutorials"
        description="Expert articles on probability, statistics, random number theory, and practical applications of RNG. Educational content for students, researchers, and professionals."
        canonical="https://numbers-generator.com/blog"
        keywords="random number blog, probability guides, statistics tutorials, RNG theory, cryptography articles"
      />
      <div className="container mx-auto px-4 py-12">
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            RandomGen Knowledge Center - RNG Blog
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Expert articles on probability, statistics, random number theory, and practical applications. 
            Learn from comprehensive guides written for students, researchers, and professionals.
          </p>
        </header>

        {/* Featured Article */}
        <section className="mb-16">
          <Card className="overflow-hidden bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
            <div className="p-8 md:p-12">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm font-semibold">
                  Featured Article
                </span>
              </div>
              <Link to={articles[0].slug} className="group">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 group-hover:text-primary transition-colors">
                  {articles[0].title}
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  {articles[0].excerpt}
                </p>
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(articles[0].date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {articles[0].readTime}
                  </span>
                  <span className="px-2 py-1 bg-accent/20 text-accent rounded text-xs font-medium">
                    {articles[0].category}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-primary font-semibold group-hover:gap-4 transition-all">
                  Explore Complete RNG Guide <ArrowRight className="w-5 h-5" />
                </div>
              </Link>
            </div>
          </Card>
        </section>

        {/* All Articles Grid */}
        <section>
          <h2 className="text-3xl font-bold mb-8">All Articles</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => (
              <Link key={article.slug} to={article.slug}>
                <Card className="p-6 h-full hover:shadow-lg hover:border-primary/50 transition-all group">
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen className="w-5 h-5 text-primary" />
                    <span className="text-xs font-semibold text-accent">
                      {article.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-muted-foreground mb-4 text-sm line-clamp-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mb-4">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(article.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {article.readTime}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-primary text-sm font-semibold group-hover:gap-3 transition-all">
                    Read Full {article.category} Article <ArrowRight className="w-4 h-4" />
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </section>

        {/* Topics Section */}
        <section className="mt-16 bg-card/30 backdrop-blur-sm rounded-lg p-8 border border-border/50">
          <h2 className="text-2xl font-bold mb-6 text-center">Topics Covered</h2>
          <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            <div className="text-center p-4">
              <h3 className="font-semibold mb-2">Fundamentals</h3>
              <p className="text-sm text-muted-foreground">RNG basics, types, algorithms, security</p>
            </div>
            <div className="text-center p-4">
              <h3 className="font-semibold mb-2">Theory</h3>
              <p className="text-sm text-muted-foreground">Probability, statistics, distributions</p>
            </div>
            <div className="text-center p-4">
              <h3 className="font-semibold mb-2">Applications</h3>
              <p className="text-sm text-muted-foreground">Real-world use cases and implementations</p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center mt-16 bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-12">
          <h2 className="text-3xl font-bold mb-4">Ready to Generate Random Numbers?</h2>
          <p className="text-xl text-muted-foreground mb-6 max-w-2xl mx-auto">
            Try our free cryptographically secure random number generator tools
          </p>
          <Link to="/">
            <button className="px-8 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors">
              Start Using Free Tools
            </button>
          </Link>
        </section>

        {/* Hero Image at Bottom */}
        <div className="mt-12 rounded-2xl overflow-hidden shadow-2xl max-w-5xl mx-auto">
          <img 
            src={blogHero} 
            alt="Educational blog and knowledge center showing articles, learning resources, and tutorials about random number generation and probability"
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </Layout>
  );
};

export default Blog;
